import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { getAuthFailed } from '../../utils/auth0Utils';
import { useAuth } from '../../hooks/useAuth';
import { STORAGE_KEYS } from '../../utils/storageKeys';

/**
 * RequireAuth component that automatically redirects to the IdP login page
 * if the user is not authenticated. Supports both Auth0 and Authentik via
 * the useAuth hook which switches based on VITE_AUTH_PROVIDER at build time.
 */
const RequireAuth = ({ children }: { children: React.ReactElement }) => {
  const { isLoading, isAuthenticated, isRedirecting, signinRedirect, error } = useAuth(true);
  const location = useLocation();

  const authFailed = getAuthFailed();

  useEffect(() => {
    const processingInvitation = localStorage.getItem(STORAGE_KEYS.PROCESSING_INVITATION) === 'true';

    if (!isLoading && !isAuthenticated && !processingInvitation) {
      signinRedirect(location.pathname);
    }

    if (isAuthenticated && processingInvitation) {
      localStorage.removeItem(STORAGE_KEYS.PROCESSING_INVITATION);
    }
  }, [isLoading, isAuthenticated, signinRedirect, location.pathname]);

  if (authFailed || error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <Typography variant="body1" color="error">
          {error?.message || 'Authentication failed. You will be redirected to the login page.'}
        </Typography>
      </Box>
    );
  }

  const processingInvitation = localStorage.getItem(STORAGE_KEYS.PROCESSING_INVITATION) === 'true';

  if ((isLoading || !isAuthenticated || isRedirecting) && !processingInvitation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <CircularProgress size={30} sx={{ mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          {isLoading
            ? 'Checking authentication...'
            : !isAuthenticated
            ? 'Redirecting to login...'
            : isRedirecting
            ? 'Redirecting to appropriate page...'
            : 'Loading your profile...'}
        </Typography>
      </Box>
    );
  }

  return children;
};

export default RequireAuth;
